import * as React from 'react';
import { fetchCatalogPresence, fetchLiveCatalogStatusIfNeeded } from '@/redux/modules/liveCatalogStatus';
import { getCatalog, getShouldShowLive } from '@/redux/modules/catalog';
import { StyledItemSaveContainer, StyledPrintContainer, TitleMenuWrapper } from './helpers/styles';
import { useDispatch, useSelector } from 'react-redux';
import CardCta from './CardCta';
import classNames from 'classnames';
import ConsoleSocial from '@/components/ConsoleSocial/ConsoleSocial';
import DetailsRow from './DetailsRow';
import { fetchLiveCatalogRegistrationIfNeeded } from '@/redux/modules/catalogRegistration';
import { getItem } from '@/redux/modules/item';
import { getLiveCatalogData } from '@/redux/modules/console';
import ImageRow from './ImageRow';
import isEqual from 'react-fast-compare';
import { isItemPromoted } from '@/redux/modules/searchPromoted';
import ItemCardMenu from './ItemCardMenu';
import loadable from '@loadable/component';
import noop from '@/utils/noop';
import PriceRow from './PriceRow';
import PromotedBadge from '@/components/Badges/PromotedBadge';
import StatusBadge from './StatusBadge';
import styles from './ItemCard.module.scss';
import TitleRow from './TitleRow';
import usePrevious from '@/hooks/usePrevious';

/** Establish connection to real-time auction events, only on the client-side */
const PubnubCatalogContainer = loadable(() => import('@/components/PubnubContainer/PubnubCatalogContainer'), {
    ssr: false,
});

type Props = {
    allowBidding?: boolean;
    className?: string;
    hideLiveButton?: boolean;
    hideLotNumbers?: boolean;
    hideSeller?: boolean;
    isLotOpen?: boolean;
    itemId: number;
    last?: boolean;
    noPubNub?: boolean;
    noWidthLimit?: boolean;
    openOnBlank?: boolean;
    showBanner?: boolean;
    showBidStatus?: boolean;
    showCta?: boolean;
    showDate?: boolean;
    showEstimate?: boolean;
    showImageBorder?: boolean;
    showImageWithHover?: boolean;
    showSoldBadge?: boolean;
    showSubMenuTrigger?: boolean;
    style?: any;
    trackClick?: (event: React.SyntheticEvent) => void;
};

const ItemCard = (props: Props) => {
    const {
        allowBidding,
        className = '',
        hideLiveButton,
        hideLotNumbers,
        hideSeller,
        isLotOpen,
        itemId,
        last,
        noPubNub,
        noWidthLimit,
        openOnBlank,
        showBanner,
        showBidStatus,
        showCta,
        showDate,
        showEstimate,
        showImageBorder,
        showImageWithHover,
        showSoldBadge,
        showSubMenuTrigger,
        style = {},
        trackClick = noop,
    } = props;

    const dispatch = useDispatch();

    const { catalogId, index, title } = useSelector((state) => getItem(state, itemId), isEqual);
    const promoted = useSelector((state) => isItemPromoted(state, itemId));
    const { currentItemIndex } = useSelector((state) => getLiveCatalogData(state, catalogId), isEqual);
    const { catalogStatus } = useSelector((state) => getCatalog(state, catalogId), isEqual);
    const comingUp = catalogStatus === 'live' && currentItemIndex <= index;
    const showLive = useSelector((state) => getShouldShowLive(state, catalogId), isEqual);
    const showSocial = allowBidding && showLive;

    const previousCatalogStatus = usePrevious(catalogStatus);

    React.useEffect(() => {
        if (previousCatalogStatus !== 'live' && catalogStatus === 'live') {
            if (allowBidding) {
                // @ts-ignore thunks can be async, fix typing with redux-toolkit maybe(?)
                dispatch(fetchLiveCatalogStatusIfNeeded(catalogId)).then(() => {
                    // After fetching the auction-state, use the correct messaging provider to get live presence count
                    dispatch(fetchCatalogPresence(catalogId));
                });
            }
            dispatch(fetchLiveCatalogRegistrationIfNeeded(catalogId));
        }
    }, [allowBidding, catalogId, catalogStatus, dispatch, previousCatalogStatus]);

    const classes = classNames(styles['item-card'], className, {
        [styles.placeholder]: !title,
        [styles['last-card']]: last,
        [styles.showSocial]: showSocial,
        [styles.open]: isLotOpen && !noWidthLimit,
    });

    const socialClasses = classNames(styles.social, { [styles.moreMargin]: !noWidthLimit });
    const itemSaveClasses = classNames(styles['item-save'], { [styles.morePadding]: !noWidthLimit });

    return (
        <div className={classes} key={`${itemId}-noWidth-${noWidthLimit}`} onClick={trackClick} style={style}>
            {comingUp && allowBidding && !noPubNub && (
                <PubnubCatalogContainer catalogId={catalogId} limitMessagesFilter="itemCard" />
            )}
            {promoted && <PromotedBadge className={!noWidthLimit ? styles.promoted : ''} />}
            <StyledItemSaveContainer className={itemSaveClasses} itemId={itemId} />
            <StyledPrintContainer noWidthLimit={noWidthLimit}>
                <ImageRow
                    itemId={itemId}
                    noWidthLimit={noWidthLimit}
                    openOnBlank={openOnBlank}
                    showBidStatus={showBidStatus}
                    showImageBorder={showImageBorder}
                    showImageWithHover={showImageWithHover}
                />
                <div>
                    {showSocial && isLotOpen && (
                        <ConsoleSocial className={socialClasses} isOnCatalogCard itemId={itemId} />
                    )}
                    {showSoldBadge && catalogStatus === 'done' && <StatusBadge />}
                    <TitleMenuWrapper>
                        <TitleRow hideLotNumbers={hideLotNumbers} itemId={itemId} openOnBlank={openOnBlank} />
                        {showSubMenuTrigger && <ItemCardMenu itemId={itemId} />}
                    </TitleMenuWrapper>
                    <DetailsRow
                        allowBidding={allowBidding}
                        hideLiveButton={hideLiveButton}
                        hideSeller={hideSeller}
                        itemId={itemId}
                        showDate={showDate}
                        showEstimate={showEstimate}
                    />
                    <PriceRow itemId={itemId} />
                    <CardCta allowBidding={allowBidding} itemId={itemId} showBanner={showBanner} showCta={showCta} />
                </div>
            </StyledPrintContainer>
        </div>
    );
};

export default React.memo<Props>(ItemCard);
